import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, Loader2, Trash2, Calendar } from "lucide-react";

function getToken() {
  return localStorage.getItem("nutramilo_access_token") || sessionStorage.getItem("nutramilo_access_token");
}

/** Upload lab image/PDF + list existing labs for a patient (doctor mode). */
export default function PatientLabUpload({ patientId, onUploaded }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState("");
  const inputId = `lab-file-${patientId}`;

  const fetchLabs = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const token = getToken();
      const { data } = await axios.get(`${API}/doctor/patients/${patientId}/labs`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setLabs(data.labs || []);
    } catch { setLabs([]); }
    finally { setLoading(false); }
  }, [API, patientId]);

  useEffect(() => { fetchLabs(); }, [fetchLabs]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = getToken();
      const fd = new FormData();
      fd.append("file", file);
      if (note) fd.append("note", note);
      const upRes = await axios.post(`${API}/doctor/patients/${patientId}/upload-lab`, fd, {
        headers: token
          ? { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
          : { "Content-Type": "multipart/form-data" },
      });
      toast.success(bg ? "Файлът е качен" : "File uploaded");
      setNote("");
      e.target.value = "";
      await fetchLabs();

      // ─── Auto-analyze: trigger AI parsing right after upload (#one-click).
      // Toast пока на user-а che обработваме без да чакаме клик.
      const newLabId = upRes.data?.id;
      if (newLabId) {
        toast.info(bg ? "Анализирам автоматично..." : "Auto-analyzing...");
        try {
          await axios.post(
            `${API}/doctor/patients/${patientId}/labs/${newLabId}/analyze`,
            {},
            { headers: token ? { Authorization: `Bearer ${token}` } : {} }
          );
          toast.success(bg ? "✓ Стойностите са добавени към профила" : "✓ Values added to profile");
          // Notify PatientLabAnalysisList и parent да рефрешнат данните.
          window.dispatchEvent(new CustomEvent("doctor-lab-uploaded"));
        } catch (analyzeErr) {
          // Soft-fail: уведомяваме че е качен, но анализът ще се повтори чрез „Анализирай" бутона.
          toast.error(bg
            ? "Авто-анализът се провали — натиснете 'Анализирай' ръчно."
            : "Auto-analysis failed — click 'Analyze' manually.");
        }
      }
      onUploaded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || (bg ? "Грешка при качване" : "Upload error"));
    } finally {
      setUploading(false);
    }
  };

  const cardCls = `rounded-2xl border p-4 sm:p-5
    ${isDark ? "bg-[hsl(223,47%,10%)] border-white/5" : "bg-white border-gray-200"}`;

  return (
    <div className={cardCls} data-testid="patient-lab-upload">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4 text-violet-500" />
        <h3 className="text-sm font-bold">{bg ? "Лабораторни изследвания" : "Lab reports"}</h3>
        <span className={`text-xs ml-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
          ({labs.length})
        </span>
      </div>

      {/* Upload */}
      <div className="space-y-2">
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={bg ? "Бележка (напр. 'Пълна кръвна картина, 01.2026')" : "Note (e.g. 'CBC panel, Jan 2026')"}
          className={`w-full px-3 py-2 text-xs rounded-lg border outline-none
            ${isDark ? "bg-[hsl(222,47%,8%)] border-white/10 text-white placeholder:text-gray-500"
                     : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400"}`}
          data-testid="lab-upload-note"
        />
        <label
          htmlFor={inputId}
          className={`w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg cursor-pointer text-xs font-semibold transition-opacity
            bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:opacity-95
            ${uploading ? "opacity-60 cursor-not-allowed" : ""}`}
          data-testid="lab-upload-label"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {uploading ? (bg ? "Качване..." : "Uploading...") : (bg ? "Качи снимка / PDF" : "Upload image / PDF")}
        </label>
        <input
          id={inputId}
          type="file"
          accept="image/*,.pdf"
          className="hidden"
          disabled={uploading}
          onChange={handleFile}
          data-testid="lab-upload-input"
        />
        <p className={`text-[10px] text-center ${isDark ? "text-gray-500" : "text-gray-500"}`}>
          {bg ? "JPG, PNG, HEIC или PDF · макс. 15 MB" : "JPG, PNG, HEIC or PDF · max 15 MB"}
        </p>
      </div>

      {/* Existing labs */}
      <div className="mt-4 pt-3 border-t border-gray-200 dark:border-white/5">
        {loading ? (
          <div className="flex items-center justify-center py-3">
            <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
          </div>
        ) : labs.length === 0 ? (
          <p className={`text-xs text-center py-2 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
            {bg ? "Все още няма качени лабораторни" : "No labs uploaded yet"}
          </p>
        ) : (
          <ul className="space-y-1.5" data-testid="lab-list">
            {labs.map((l) => {
              const isPdf = (l.content_type || "").includes("pdf") || l.filename?.toLowerCase().endsWith(".pdf");
              const Icon = isPdf ? FileText : ImageIcon;
              return (
                <li
                  key={l.id}
                  className={`flex items-start gap-2 p-2 rounded-lg text-xs
                    ${isDark ? "bg-[hsl(222,47%,8%)]" : "bg-gray-50"}`}
                  data-testid={`lab-item-${l.id}`}
                >
                  <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${isPdf ? "text-rose-500" : "text-sky-500"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{l.filename}</p>
                    {l.note && <p className={`text-[10px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>{l.note}</p>}
                    <p className={`text-[10px] flex items-center gap-1 mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                      <Calendar className="w-2.5 h-2.5" />
                      {new Date(l.created_at).toLocaleString(bg ? "bg-BG" : "en-US")}
                      · {Math.round((l.size || 0) / 1024)} KB
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
